import { renderImpressions } from '@mntn/formatters'
import { cn, Icon, Text } from '@mntn/ui-components'
import { useCallback } from 'react'
import type { OrderItem } from '@/flight-deck-api/services/order-items.service'
import { CampaignActionsMenu } from './campaign-actions-menu'
import { StatusBadge } from './status-badge'
import { DateCell, TextCell } from './table-cells'
import type { ArchiveCampaignCallback, DuplicateCampaignCallback } from './types/types'
import { formatMoney } from './utils'

type CampaignGroupAction = NonNullable<OrderItem['campaignGroups'][number]['availableActions']>[number]

interface Props {
  campaigns: OrderItem['campaignGroups']
  onDuplicateCampaign: DuplicateCampaignCallback
  onArchiveCampaign: ArchiveCampaignCallback
  layout?: 'compact'
  variant?: 'dark'
  className?: string
  isLoading?: boolean
}

export function CampaignInfo({
  campaigns,
  onDuplicateCampaign,
  onArchiveCampaign,
  variant,
  className,
  isLoading = false,
}: Props) {
  return (
    <div
      className={cn(
        'relative overflow-x-auto outline-1 outline-offset-[-1px]',
        variant === 'dark' ? 'outline-header' : 'outline-gray-g1',
      )}
    >
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-alpha-sub-surface-bg backdrop-blur-[6px]">
          <div className="rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 bg-background border border-border">
            <Icon name="LoaderCircle" className="w-5 h-5 animate-spin text-primary-accent" />
            <Text variant="base-sm" className="font-medium text-header">
              Loading...
            </Text>
          </div>
        </div>
      )}

      {/* Header row */}
      <div
        className={cn(
          'grid min-w-[860px] grid-cols-[120px_minmax(160px,3fr)_minmax(80px,1fr)_minmax(80px,1fr)_minmax(90px,1fr)_minmax(90px,1fr)_minmax(90px,1fr)_48px] gap-2 h-16 pl-6 pr-3 items-center bg-sub-surface-bg',
          variant === 'dark' && 'bg-background-inverse',
          className,
        )}
      >
        <Header variant={variant}>Status</Header>
        <Header variant={variant}>Campaign Name</Header>
        <Header variant={variant} align="left">
          Start
        </Header>
        <Header variant={variant} align="left">
          End
        </Header>
        <Header variant={variant} align="left">
          Impressions
        </Header>
        <Header variant={variant} align="left">
          Budget
        </Header>
        <Header variant={variant} align="left">
          Spend
        </Header>
        <div className="w-12" />
      </div>

      {/* Campaign rows */}
      {campaigns.map(campaign => (
        <CampaignInfoRow
          key={campaign.campaignGroupId}
          campaign={campaign}
          onViewCampaign={() => {
            window.location.href = `/campaign/${campaign.campaignGroupId}?returnTo=flightdeck`
          }}
          onDuplicateCampaign={onDuplicateCampaign}
          onArchiveCampaign={onArchiveCampaign}
          onRowClick={() => {
            window.location.href = `/campaign/${campaign.campaignGroupId}?returnTo=flightdeck`
          }}
          variant={variant}
          className={className}
          isLoading={isLoading}
        />
      ))}
    </div>
  )
}

const Header = ({
  children,
  align = 'left',
  visuallyHidden = false,
  variant,
}: {
  children: React.ReactNode
  align?: 'left' | 'right' | 'center'
  visuallyHidden?: boolean
  variant?: 'dark'
}) => (
  <Text
    variant="sm-sb"
    inline
    className={cn(
      'text-header m-0',
      variant === 'dark' && 'text-header-inverse',
      align === 'right' && 'text-right',
      align === 'left' && 'text-left',
      align === 'center' && 'text-center',
      visuallyHidden && 'sr-only',
    )}
  >
    {children}
  </Text>
)

const CampaignInfoRow = ({
  campaign,
  onViewCampaign,
  onDuplicateCampaign,
  onArchiveCampaign,
  onRowClick,
  variant,
  className,
  isLoading = false,
}: {
  campaign: OrderItem['campaignGroups'][0]
  onViewCampaign: () => void
  onArchiveCampaign: ArchiveCampaignCallback
  onDuplicateCampaign: DuplicateCampaignCallback
  onRowClick: ({ campaignId }: { campaignId: number }) => void
  variant?: 'dark'
  className?: string
  isLoading?: boolean
}) => {
  // An archived campaign is listed for reference only: its campaign page and the actions
  // that operate on it no longer apply, so the row is inert.
  const isArchived = campaign.status === 'ARCHIVED' || campaign.status === 'LEGACY_ARCHIVED'

  const handleRowClick = useCallback(() => {
    onRowClick({ campaignId: campaign.campaignGroupId })
  }, [campaign.campaignGroupId, onRowClick])

  const handleCampaignAction = useCallback(
    (action: CampaignGroupAction, campaign: OrderItem['campaignGroups'][0]) => {
      switch (action) {
        case 'VIEW':
          onViewCampaign()
          break
        case 'DUPLICATE':
          onDuplicateCampaign(campaign.campaignGroupId, false)
          break
        case 'DUPLICATE_AS_EXPERIMENT':
          onDuplicateCampaign(campaign.campaignGroupId, true)
          break
        case 'ARCHIVE':
          onArchiveCampaign(campaign.campaignGroupId)
          break
        default:
          console.warn(`Unhandled campaign action: ${action}`)
      }
    },
    [onViewCampaign, onDuplicateCampaign, onArchiveCampaign],
  )

  return (
    <div
      className={cn(
        'grid min-w-[860px] grid-cols-[120px_minmax(160px,3fr)_minmax(80px,1fr)_minmax(80px,1fr)_minmax(90px,1fr)_minmax(90px,1fr)_minmax(90px,1fr)_48px] gap-2 min-h-16 pl-6 pr-3 border-b border-surface-bg bg-background items-center w-full text-left transition-colors overflow-visible text-primary',
        !isArchived && 'hover:bg-surface-bg cursor-pointer',
        variant === 'dark' && 'bg-sub-surface-bg-inverse border-surface-bg-inverse',
        variant === 'dark' && !isArchived && 'hover:bg-surface-bg-inverse',
        className,
      )}
      style={{ overflow: 'visible' }}
    >
      <a
        className="col-span-7 grid grid-cols-subgrid gap-2 items-center h-full"
        href={isArchived ? undefined : `/campaign/${campaign.campaignGroupId}?returnTo=flightdeck`}
        aria-disabled={isArchived}
        onClick={isArchived ? undefined : handleRowClick}
        onKeyDown={
          isArchived
            ? undefined
            : e => {
                if (e.key === ' ') {
                  e.preventDefault()
                  handleRowClick()
                }
              }
        }
      >
        {/* Status */}
        <div>
          <StatusBadge status={campaign.status} variant={variant} />
        </div>

        {/* Campaign Name */}
        <div>
          <TextCell text={campaign.name} className="text-left" variant={variant} />
        </div>

        {/* Start Date */}
        <div className="min-w-[80px]">
          <DateCell date={campaign.startTime ?? undefined} className="text-left" variant={variant} />
        </div>

        {/* End Date */}
        <div className="min-w-[80px]">
          <DateCell date={campaign.endTime ?? undefined} className="text-left" variant={variant} />
        </div>

        {/* Impressions */}
        <div className="min-w-[90px]">
          <TextCell
            text={campaign.totalImpressions != null ? renderImpressions(campaign.totalImpressions) : '0'}
            className="text-left"
            variant={variant}
          />
        </div>

        {/* Budget */}
        <div className="min-w-[90px]">
          <TextCell text={formatMoney(campaign.totalBudget ?? 0)} className="text-left" variant={variant} />
        </div>

        {/* Spend */}
        <div className="min-w-[90px]">
          <TextCell
            text={campaign.spend != null ? formatMoney(campaign.spend) : '--'}
            className="text-left"
            variant={variant}
          />
        </div>
      </a>

      {/* Actions Menu */}
      <div className="w-12 flex justify-center overflow-visible" style={{ overflow: 'visible' }}>
        {!isArchived && (
          <CampaignActionsMenu
            variant={variant}
            campaign={campaign}
            availableActions={campaign.availableActions}
            onAction={handleCampaignAction}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  )
}
